/**
 * Shared requirements/defect coverage display vocabulary.
 *
 * The Requirements covered and Defects covered cards and their gap lists
 * import from here so they speak with one voice: one tooltip per card,
 * one rule for a row's label and color, one link to a row's proposed tests.
 * The percentage pill reuses `coveragePillClass` from `testCoverage.ts`
 * (one threshold-to-color scale).
 *
 * A requirement (or defect) comes from either source: a part of a
 * document marked Requirements (or Defects), or an issue from the
 * connected tracker. Wording follows the mockup
 * `docs/mockups/requirements-from-every-source.html`.
 */
import type { QueryClient } from '@tanstack/react-query'
import type { IssueCoverageRow, IssueCoverageState } from '@/types'

/** First element of the query key for the one live coverage read. */
export const COVERAGE_QUERY_KEY = 'coverage'
/** First element of the query key for the requirements/defects trend. */
export const ISSUE_COVERAGE_TREND_QUERY_KEY = 'issue-coverage-trend'

/** Mark every coverage number and trend as out of date, for every app and
 * variant. Call after anything that changes coverage: a test, a script, a
 * proposed test, a document's kind, a document added or deleted, or the
 * issue tracker's connection or type mapping. The server works every
 * number and trend point out fresh with today's settings, so the next
 * view must ask again rather than show a cached copy from before. */
export function invalidateCoverage(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [COVERAGE_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [ISSUE_COVERAGE_TREND_QUERY_KEY] }),
  ]).then(() => undefined)
}

/** Tooltip for the Requirements covered card. Names every source, and
 * says what the second line counts. */
export const REQUIREMENTS_COVERAGE_TOOLTIP =
  "A requirement is something the app must do: a part of a document you marked as Requirements and confirmed, or a story from your issue tracker. It's covered when at least one of its proposed tests has a passing test. A requirement with no proposed tests yet counts as not covered. The second line counts every proposed test that has a script, including ones from reference guides."

/** Tooltip for the Defects covered card, the same rule for bugs. A
 * passing test is evidence the bug is checked, not proof it can never
 * come back. */
export const DEFECTS_COVERAGE_TOOLTIP =
  "A defect is a bug that must not come back: a part of a document you marked as Defects and confirmed, or a bug from your issue tracker. It's covered when at least one of its proposed tests has a passing test. A defect with no proposed tests yet counts as not covered. A passing test means the bug is being checked for, not a guarantee it can never happen again."

/** Labels for one card and its gap list. Requirements and defects render
 * the same components with a different set. */
export interface IssueGapLabels {
  /** Gap list header. */
  title: string
  /** In-flight line. */
  loading: string
  /** Load-error line. */
  loadError: string
  /** Success line when nothing is uncovered. */
  allCovered: string
  /** Card empty state, before the link: "No requirements yet." */
  emptyLead: string
  /** The empty state's link to Sources. */
  emptyLink: string
  /** After the link. */
  emptyTail: string
}

export const REQUIREMENTS_GAP_LABELS: IssueGapLabels = {
  title: 'Requirements without a passing test',
  loading: 'Loading requirements coverage…',
  loadError: "Couldn't load requirements coverage.",
  allCovered: 'Every requirement has a passing test.',
  emptyLead: 'No requirements yet.',
  emptyLink: 'Mark a document as Requirements',
  emptyTail: 'or connect an issue tracker.',
}

export const DEFECTS_GAP_LABELS: IssueGapLabels = {
  title: 'Defects without a passing test',
  loading: 'Loading defect coverage…',
  loadError: "Couldn't load defect coverage.",
  allCovered: 'Every defect has a passing test.',
  emptyLead: 'No defects yet.',
  emptyLink: 'Mark a document as Defects',
  emptyTail: 'or connect an issue tracker.',
}

/** The label a gap row shows, one per state, so the row says plainly
 * what it is missing. With no proposed tests it is "Not covered"; with
 * proposed tests but no script, "Not scripted". One with scripts, none
 * passing, says "Failing" when a script failed its last run (the more
 * specific fact) and "Scripted, not passing" otherwise. Wording follows
 * the mockup `docs/mockups/2703-coverage-words.html`. */
export function gapRowStateLabel(row: Pick<IssueCoverageRow, 'state' | 'hasFailingTest'>): string {
  switch (row.state) {
    case 'covered':
      return 'Covered'
    case 'failing':
      return row.hasFailingTest ? 'Failing' : 'Scripted, not passing'
    case 'has-candidate':
      return 'Not scripted'
    default:
      return 'Not covered'
  }
}

/** A gap row's count: "No tests proposed yet" when it has none, else
 * "N of M proposed tests scripted" (singular when M is 1). */
export function gapRowCountLabel(row: Pick<IssueCoverageRow, 'ideaCount' | 'scriptedCount'>): string {
  if (row.ideaCount === 0) return 'No tests proposed yet'
  return `${row.scriptedCount} of ${row.ideaCount} proposed ${row.ideaCount === 1 ? 'test' : 'tests'} scripted`
}

/** Pill classes for a gap row's label (light + dark). Only a failing
 * script is red; every other gap is a plain grey, and covered is green. */
export function gapRowStatePillClass(row: Pick<IssueCoverageRow, 'state' | 'hasFailingTest'>): string {
  if (row.state === 'covered') {
    return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900'
  }
  if (row.state === 'failing' && row.hasFailingTest) {
    return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900'
  }
  return 'bg-muted text-muted-foreground border-border'
}

/** Is this row an actionable gap (anything short of a passing test)? The
 * gap list shows exactly these. */
export function isUncovered(state: IssueCoverageState): boolean {
  return state !== 'covered'
}

/** The query string that opens the Proposed tab filtered to one row's
 * proposed tests: a document part by its document and part, an issue by its
 * connection and id. `appId` and `variantId` travel too, so the Tests
 * page switches to the right app first. */
export function gapRowIdeasQuery(row: IssueCoverageRow, appId: string, variantId: string): string {
  const q = new URLSearchParams({ appId, variantId, stage: 'proposed' })
  if (row.source === 'document') {
    q.set('referenceDocId', row.documentId)
    q.set('partId', row.partId)
  } else {
    q.set('issueConnectionId', row.connectionId)
    q.set('issueExternalId', row.externalId)
  }
  return q.toString()
}

/** The query string that opens Sources on the Proposed tab, where a
 * document can be marked Requirements or Defects. */
export function sourcesQuery(appId: string, variantId: string): string {
  return new URLSearchParams({ appId, variantId, stage: 'proposed', sources: 'open' }).toString()
}
